import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Checkbox,
  Row,
  Col,
  Spin,
  message,
  Popconfirm,
  Button,
  Alert,
} from "antd";
import TextArea from "antd/es/input/TextArea";

const { Option } = Select;

export interface TItemModuleOption {
  id: number;
  key: string;
  type: string;
  string_value: string;
  numeric_value?: number;
  show: boolean;
}

export interface StorageItemBasic {
  id?: number;
  uid?: string;
  name: string;
  description: string;
  register_type: string;
  measure_unit?: string;
  category?: string;
  sell_item: boolean;
  purchase_item: boolean;
  sell_price?: number;
  purchase_price?: number;
  sku?: string;
  custom_id?: string;
  is_traceable?: boolean;
}

interface UnifiedItemModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: (item: any) => void;
  onSave: (values: StorageItemBasic) => Promise<any>;
  loading?: boolean;
  /** List of item type options from module config (type === 'ITEM_TYPE') */
  itemTypes?: TItemModuleOption[];
  /** List of UM options from module config (type === 'UM') */
  umOptions?: TItemModuleOption[];
  /** List of category options from module config (type === 'CATEGORY_OPTION') */
  categoryOptions?: TItemModuleOption[];
  /** Default register type to select when modal opens (e.g. 'SERVICE', 'PRODUCT') */
  defaultRegisterType?: string;
  /** Default name to pre-fill */
  defaultName?: string;
}

const getModalTitle = (registerType: string | undefined, itemTypes: TItemModuleOption[] = []): string => {
  if (!registerType) return "Nuevo Elemento de Catálogo";
  const found = itemTypes.find((t) => t.key === registerType);
  if (found?.string_value) return `Nuevo ${found.string_value}`;
  if (registerType === "SERVICE") return "Nuevo Servicio / Costo";
  if (registerType === "PRODUCT") return "Nuevo Producto / Insumo";
  return "Nuevo Elemento de Catálogo";
};

export const UnifiedItemModal: React.FC<UnifiedItemModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  onSave,
  loading: externalLoading,
  itemTypes = [],
  umOptions = [],
  categoryOptions = [],
  defaultRegisterType,
  defaultName,
}) => {
  const [form] = Form.useForm();
  const [localLoading, setLocalLoading] = useState(false);
  const registerType: string | undefined = Form.useWatch("register_type", form);
  const sellItem: boolean = Form.useWatch("sell_item", form);
  const purchaseItem: boolean = Form.useWatch("purchase_item", form);

  const loading = externalLoading || localLoading;
  const isService = registerType === "SERVICE";
  const isProduct = Boolean(registerType && registerType !== "SERVICE");
  const hasRegisterType = Boolean(registerType);

  // Filtered options respecting show = true and deduplicated by id/key
  const visibleItemTypes = itemTypes
    .filter((o) => o.show)
    .filter((opt, index, self) => index === self.findIndex((t) => (t.id && opt.id ? t.id === opt.id : t.key === opt.key)));
  const visibleUmOptions = umOptions
    .filter((o) => o.show)
    .filter((opt, index, self) => index === self.findIndex((t) => (t.id && opt.id ? t.id === opt.id : t.key === opt.key)));
  const visibleCategoryOptions = categoryOptions
    .filter((o) => o.show)
    .filter((opt, index, self) => index === self.findIndex((t) => (t.id && opt.id ? t.id === opt.id : t.key === opt.key)));

  // When no categories are available, we use 'VRS' silently
  const hasCategories = visibleCategoryOptions.length > 0;

  useEffect(() => {
    if (visible) {
      form.resetFields();
      const initialType = defaultRegisterType || (visibleItemTypes.length === 1 ? visibleItemTypes[0].key : undefined);
      if (initialType) {
        form.setFieldsValue({ register_type: initialType });
      }
      if (defaultName) {
        form.setFieldsValue({ name: defaultName });
      }
      // Set default UM if there's only one option
      if (visibleUmOptions.length === 1) {
        form.setFieldsValue({ measure_unit: visibleUmOptions[0].key });
      }
      // Set default category if there's only one option
      if (visibleCategoryOptions.length === 1) {
        form.setFieldsValue({ category: visibleCategoryOptions[0].key });
      }
      if (initialType === "SERVICE") {
        form.setFieldsValue({
          purchase_item: true,
          sell_item: false,
        });
      }
    }
  }, [visible, form, visibleItemTypes.length, visibleUmOptions.length, visibleCategoryOptions.length, defaultRegisterType, defaultName]);


  const handleFinish = async (values: any) => {
    setLocalLoading(true);
    try {
      const result = await onSave(values as StorageItemBasic);
      onSuccess(result);
      form.resetFields();
    } catch (error: any) {
      message.error(error.message || "Error al guardar el elemento");
    } finally {
      setLocalLoading(false);
    }
  };

  const formFooter = [
    <Button key="cancel" onClick={onCancel}>
      Cancelar
    </Button>,
    <Popconfirm
      key="confirm"
      title="¿Estás seguro de realizar esta acción?"
      onConfirm={() => form.submit()}
      okText="Sí"
      cancelText="No"
      disabled={visibleItemTypes.length === 0}
    >
      <Button type="primary" htmlType="button" loading={loading} disabled={visibleItemTypes.length === 0}>
        Crear
      </Button>
    </Popconfirm>,
  ];

  return (
    <Modal
      title={<div style={{ textAlign: "center" }}>{getModalTitle(registerType, itemTypes)}</div>}
      open={visible}
      onCancel={onCancel}
      footer={formFooter}
      maskClosable={false}
      destroyOnHidden
      width={640}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{
            sell_item: true,
            purchase_item: true,
          }}
          style={{ paddingTop: 8 }}
          onSubmitCapture={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <Row gutter={16}>
            {/* Tipo de registro */}
            <Col sm={12} xs={24}>
              <Form.Item
                label="Tipo de registro"
                name="register_type"
                rules={[{ required: true, message: "Selecciona el tipo" }]}
              >
                {visibleItemTypes.length > 0 ? (
                  <Select placeholder="Selecciona tipo">
                    {visibleItemTypes.map((opt) => (
                      <Option key={opt.key} value={opt.key}>
                        {opt.string_value || opt.key}
                      </Option>
                    ))}
                  </Select>
                ) : (
                  <Alert
                    type="warning"
                    showIcon
                    message="No hay tipos de registro configurados para esta compañía. Configúralos en el módulo de administración."
                    style={{ fontSize: 12 }}
                  />
                )}
              </Form.Item>
            </Col>

            {/* Nombre */}
            <Col sm={12} xs={24}>
              <Form.Item
                label="Nombre"
                name="name"
                rules={[{ required: true, message: "Ingresa el nombre" }]}
              >
                <Input 
                  maxLength={200} 
                  placeholder={isService ? "Ej: Flete de mercancía, Transporte, Mano de obra..." : "Ej: Fertilizante 20-20-20, Insumo..."} 
                />
              </Form.Item>
            </Col>

            {/* Descripción */}
            <Col xs={24}>
              <Form.Item
                label="Descripción"
                name="description"
                rules={[{ required: true, message: "Ingresa la descripción" }]}
              >
                <TextArea
                  maxLength={500}
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  placeholder={isService ? "Detalle o especificación del servicio / gasto" : "Descripción del elemento"}
                />
              </Form.Item>
            </Col>

            {/* Identificador / SKU */}
            <Col sm={12} xs={24}>
              <Form.Item label="Identificador (SKU / Código)" name="custom_id">
                <Input maxLength={50} placeholder="Código interno (opcional)" />
              </Form.Item>
            </Col>

            {/* Categoría – solo si hay opciones disponibles */}
            {isProduct && hasCategories && (
              <Col sm={12} xs={24}>
                <Form.Item
                  label="Categoría"
                  name="category"
                  rules={[{ required: true, message: "Selecciona la categoría" }]}
                >
                  <Select placeholder="Selecciona categoría">
                    {visibleCategoryOptions.map((opt) => (
                      <Option key={opt.key} value={opt.key}>
                        {opt.string_value || opt.key}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}

            {/* Unidad de medida – solo para productos */}
            {isProduct && (
              <Col sm={12} xs={24}>
                <Form.Item
                  label="Unidad de medida"
                  name="measure_unit"
                  rules={[{ required: true, message: "Selecciona la unidad de medida" }]}
                >
                  {visibleUmOptions.length > 0 ? (
                    <Select placeholder="Selecciona unidad">
                      {visibleUmOptions.map((opt) => (
                        <Option key={opt.key} value={opt.key}>
                          {opt.string_value || opt.key}
                        </Option>
                      ))}
                    </Select>
                  ) : (
                    <Alert
                      type="warning"
                      showIcon
                      message="No hay unidades de medida configuradas. Configura las unidades en el módulo de almacén."
                      style={{ fontSize: 12 }}
                    />
                  )}
                </Form.Item>
              </Col>
            )}
          </Row>

          {/* Opciones de habilitación */}
          {hasRegisterType && (
            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item label="Habilitaciones">
                  <Row gutter={16}>
                    <Col>
                      <Form.Item name="sell_item" valuePropName="checked" noStyle>
                        <Checkbox>Habilitar en ventas</Checkbox>
                      </Form.Item>
                    </Col>
                    <Col>
                      <Form.Item name="purchase_item" valuePropName="checked" noStyle>
                        <Checkbox>{isService ? "Habilitar en compras (Costos / Gastos directos)" : "Habilitar en compras"}</Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* Precios */}
          {hasRegisterType && (
            <Row gutter={16}>
              {sellItem && (
                <Col sm={12} xs={24}>
                  <Form.Item label="Precio de venta" name="sell_price">
                    <InputNumber
                      placeholder="0"
                      prefix="$"
                      style={{ width: "100%" }}
                      min={0}
                      formatter={(value) =>
                        `${value}`.replaceAll(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) => value?.replaceAll(/\$\s?|(,*)/g, "") as any}
                    />
                  </Form.Item>
                </Col>
              )}
              {purchaseItem && (
                <Col sm={12} xs={24}>
                  <Form.Item label={isService ? "Precio de compra (Costo)" : "Precio de compra"} name="purchase_price">
                    <InputNumber
                      placeholder="0"
                      prefix="$"
                      style={{ width: "100%" }}
                      min={0}
                      formatter={(value) =>
                        `${value}`.replaceAll(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) => value?.replaceAll(/\$\s?|(,*)/g, "") as any}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};
